import { Injectable, Injector } from '@angular/core';
import { RequestService } from './request.service';
import { Specialty } from '../models/specialty.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { DialogComponent } from '../modules/shared/components/dialog/dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Doctor } from '../models/doctor.model';
import { AppointmentBlock } from '../models/appointment-block.model';
import { Patient } from '../models/patient.model';
import { AppointmentData } from '../models/appointment-data.model';
import { Appointment } from '../models/appointment.model';
import * as moment from 'moment';
import { AppointmentStepperService } from './appointment-stepper.service';

@Injectable({
  providedIn: 'root'
})
export class AppointmentService {

  private appointmentDataSubject!: BehaviorSubject<AppointmentData>;

  constructor(
    private matDialog: MatDialog,
    private requestService: RequestService,
    private injector: Injector
  ) {
    let appointmentData: AppointmentData | string | null = localStorage.getItem('appointment_data');

    if (appointmentData) {
      appointmentData = JSON.parse(appointmentData);
    } else {
      appointmentData = {};
    }

    this.appointmentDataSubject = new BehaviorSubject<AppointmentData>(appointmentData as AppointmentData);

    this.appointmentData$.subscribe({
      next: (appointmentData: AppointmentData) => {
        localStorage.setItem('appointment_data', JSON.stringify(appointmentData));
      },
    });
  }

  set appointmentData(appointmentData: AppointmentData) {
    this.appointmentDataSubject.next(appointmentData);
  }

  get appointmentData(): AppointmentData {
    return this.appointmentDataSubject.value;
  }

  get appointmentData$(): Observable<AppointmentData> {
    return this.appointmentDataSubject.asObservable();
  }

  get specialty(): Specialty | undefined {
    return this.appointmentData.specialty;
  }

  set specialty(specialty: Specialty | undefined) {
    this.appointmentData.specialty = specialty;
    this.appointmentDataSubject.next(this.appointmentData);
  }

  get doctor(): Doctor | undefined {
    return this.appointmentData.doctor;
  }

  set doctor(doctor: Doctor | undefined) {
    this.appointmentData.doctor = doctor;
    this.appointmentDataSubject.next(this.appointmentData);
  }

  get useAllDoctors(): boolean | undefined {
    return this.appointmentData.useAllDoctors;
  }

  set useAllDoctors(useAllDoctors: boolean | undefined) {
    this.appointmentData.useAllDoctors = useAllDoctors;
    this.appointmentDataSubject.next(this.appointmentData);
  }

  get patient(): Patient | undefined {
    return this.appointmentData.patient;
  }

  set patient(patient: Patient | undefined) {
    this.appointmentData.patient = patient;
    this.appointmentDataSubject.next(this.appointmentData);
  }

  get block(): Partial<AppointmentBlock> | undefined {
    return this.appointmentData.block;
  }

  set block(block: Partial<AppointmentBlock> | undefined) {
    this.appointmentData.block = block;
    this.appointmentDataSubject.next(this.appointmentData);
  }

  set skipSelectCaller(skip: boolean) {
    localStorage.setItem('appointmentSkipSelectCaller', JSON.stringify(skip));
  }

  get skipSelectCaller(): boolean {
    return localStorage.getItem('appointmentSkipSelectCaller') === 'true';
  }

  set skipSelectSpecialty(skip: boolean) {
    localStorage.setItem('appointmentSkipSelectSpecialty', JSON.stringify(skip));
  }

  get skipSelectSpecialty(): boolean {
    return localStorage.getItem('appointmentSkipSelectSpecialty') === 'true';
  }

  setAppointment(appointmentId: string): void {
    localStorage.setItem("appointment", appointmentId);
  }

  clearAppointment(): void {
    localStorage.removeItem("appointment");
    localStorage.removeItem("appointmentStatus");
    localStorage.removeItem("appointment_data");
    if (this.isAppointmentShortFlow() && this.specialty) {
      this.appointmentDataSubject.next({ specialty: this.specialty });
    } else {
      this.appointmentDataSubject.next({});
    }
  }

  getAppointmentId(): string | null {
    return localStorage.getItem("appointment");
  }

  inAppointment(): boolean {
    return this.getAppointmentId() != null;
  }

  setAppointmentStatus(appointmentStatus: string): void {
    localStorage.setItem("appointmentStatus", appointmentStatus);
  }

  getAppointmentStatus (): string | null {
    return localStorage.getItem("appointmentStatus");
  }

  available(appointmentId : string): any {
    try {
      return this.requestService.get("/appointment/" + appointmentId + "/available");
    } catch (err : any) {
      return err?._body;
    }
  }

  async validateAppointment(appointmentId: string) {
    try {
      await this.requestService.get("/appointment/"+appointmentId+"/available")
      return { valid: true };
    } catch (err : any) {
      const error = err?.error || err;
      let message;

      if (error?.appointmentStatus === 'ended')
        message = 'dialog_appointment_error_ended';
      else if (error?.appointmentStatus === 'ended_waiting_tolerance')
        message = 'dialog_appointment_error_ended_waiting_tolerance';
      if (error?.name == 'notFound')
        message = 'dialog_appointment_error_not_found';

      return { valid: false, message: message || error?.message };
    }
  }

  getAppointment(appointmentId: string){
    return this.requestService.get('/appointment/' + appointmentId)
  }

  getAppointments(params?: any) {
    return this.requestService.get('/appointment', params);
  }

  openAppointmentErrorDialog(message: string) {
    this.matDialog.open(DialogComponent, {
      width: '80%',
      maxWidth: '350px',
      data: {
        message: message,
        title: 'dialog_title_warning',
        canCancel: false,
        actions: [{
          message: "dialog_accept",
          action: () => {}
        }]
      },
      disableClose: true,
    });
  }

  getBlocksByDateAndDoctor(
    date: string /* DD-MM-YYYY */,
    doctorId?: string,
    action = 'next',
    specialtyId?: string,
    useAllDoctors = false,
    daysOfWeek?: number[],
    time?: {startTime: string, endTime: string},
  ) {
    const params: any = { date, action };
    if(doctorId) params.doctor = doctorId;
    if(specialtyId) params.specialty = specialtyId;
    if(useAllDoctors) params.groupByDoctor = true;
    if(daysOfWeek) params.daysOfWeek = daysOfWeek;
    if(time) {
      params.startTime = time.startTime;
      params.endTime = time.endTime;
    }

    return this.requestService.get('/patient/blocks', params);
  }

  createAppointment(patientId: string, specialtyId: string, blockId: string) {
    return this.requestService.post('/appointment', { patient: patientId, specialty: specialtyId, block: blockId });
  }

  cancelAppointment(appointmentId: string) {
    return this.requestService.put(`/appointment/${appointmentId}/cancel`, {});
  }

  getSpecialtiesAndDoctorsHistory(): Promise<{specialties: Partial<Specialty>[], hasAppointmentsWithoutSpecialty: boolean, doctors: Partial<Doctor>[]}> {
    return this.requestService.get(`/appointment/specialtiesAndDoctors`);
  }

  isEnded(appointment?: Appointment): boolean {
    return moment().isAfter(moment(appointment?.endDate));
  }

  isAppointmentShortFlow(): boolean {
    return localStorage.getItem('appointmentShortFlow') === 'true';
  }

  endBookAppointmentFlow(): void {
    localStorage.removeItem('appointmentShortFlow');
    localStorage.removeItem('appointmentSkipSelectCaller');
    localStorage.removeItem('appointmentSkipSelectSpecialty');
    this.injector.get(AppointmentStepperService).reset();
  }
}
